'use client';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Check, Phone } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { PatternFormat } from 'react-number-format';

type PhoneCountry = {
  code: string;
  name: string;
  prefix: string;
  flag: string;
  pattern: string;
};

export const AVAILABLE_COUNTRIES: PhoneCountry[] = [
  {
    code: 'VE',
    name: 'Venezuela',
    prefix: '+58',
    flag: '🇻🇪',
    pattern: '### #######',
  },
  {
    code: 'CO',
    name: 'Colombia',
    prefix: '+57',
    flag: '🇨🇴',
    pattern: '##########',
  },
  {
    code: 'PA',
    name: 'Panamá',
    prefix: '+507',
    flag: '🇵🇦',
    pattern: '####-####',
  },
  {
    code: 'US',
    name: 'USA',
    prefix: '+1',
    flag: '🇺🇸',
    pattern: '(###) ###-####',
  },
  {
    code: 'ES',
    name: 'España',
    prefix: '+34',
    flag: '🇪🇸',
    pattern: '### ### ###',
  },
];

export const DEFAULT_COUNTRIES: PhoneCountry[] = [AVAILABLE_COUNTRIES[0]];

interface InternationalPhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: boolean;
  disabled?: boolean;
  countries?: PhoneCountry[];
}

export function InternationalPhoneInput({
  value = '',
  onChange,
  onBlur,
  error,
  disabled,
  countries = DEFAULT_COUNTRIES,
}: InternationalPhoneInputProps) {
  const safeCountries = countries.length ? countries : DEFAULT_COUNTRIES;

  const currentCountry = useMemo(() => {
    const safeValue = value || '';
    const found = AVAILABLE_COUNTRIES.find((country) => safeValue.startsWith(country.prefix));
    return found || safeCountries[0];
  }, [safeCountries, value]);

  const [selectedCountry, setSelectedCountry] = useState(currentCountry);

  useEffect(() => {
    setSelectedCountry(currentCountry);
  }, [currentCountry]);

  const safeValue = value || '';
  const displayValue = safeValue.startsWith(selectedCountry.prefix)
    ? safeValue.slice(selectedCountry.prefix.length)
    : safeValue;

  const showCountrySelector = safeCountries.length > 1;

  return (
    <div className="flex w-full flex-col gap-1.5">
      <div
        className={cn(
          'flex h-16 w-full overflow-hidden rounded-2xl border-2 border-slate-100 bg-white shadow-sm transition-all focus-within:border-slate-900',
          error && 'border-rose-500 bg-rose-50/30 focus-within:border-rose-500',
          disabled && 'cursor-not-allowed bg-slate-50 opacity-50',
        )}
      >
        {showCountrySelector ? (
          <div className="group relative h-full w-[70px] shrink-0 border-r border-slate-100 bg-slate-50">
            <select
              disabled={disabled}
              value={selectedCountry.code}
              onChange={(e) => {
                const country = safeCountries.find((item) => item.code === e.target.value);
                if (country) {
                  setSelectedCountry(country);
                  onChange('');
                }
              }}
              className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
            >
              {safeCountries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.flag} {country.prefix}
                </option>
              ))}
            </select>

            <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-2xl">
              {selectedCountry.flag}
            </div>
          </div>
        ) : (
          <div className="flex h-full w-[62px] shrink-0 items-center justify-center border-r border-slate-100 bg-slate-50 px-2 text-center">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
              {selectedCountry.prefix}
            </span>
          </div>
        )}

        <div className="relative flex flex-1 items-center bg-transparent">
          {showCountrySelector ? (
            <div className="pointer-events-none absolute left-4 flex items-center">
              <span className="text-base font-black text-slate-400">
                {selectedCountry.prefix}
              </span>
            </div>
          ) : null}

          <PatternFormat
            disabled={disabled}
            customInput={Input}
            format={selectedCountry.pattern}
            mask="_"
            value={displayValue}
            onBlur={onBlur}
            onValueChange={(values) => {
              let rawNum = values.value;

              if (rawNum.startsWith('0')) {
                rawNum = rawNum.substring(1);
              }

              onChange(rawNum ? `${selectedCountry.prefix}${rawNum}` : '');
            }}
            placeholder={selectedCountry.pattern.replace(/#/g, '0')}
            className={cn(
              'h-full w-full border-none bg-transparent pr-12 text-lg font-black tracking-wider placeholder:text-slate-200 focus-visible:ring-0',
              showCountrySelector ? 'pl-[4.5rem]' : 'pl-4',
            )}
          />

          <div className="pointer-events-none absolute right-4">
            {displayValue.length >= (selectedCountry.pattern.match(/#/g)?.length || 10) ? (
              <Check className="h-5 w-5 text-emerald-500" />
            ) : (
              <Phone className="h-5 w-5 text-slate-200" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
